import { computed, onBeforeUnmount, type Ref, watch } from 'vue'

import { isShowVideoStreamStats, PreferenceKey, usePreferencesStore } from '@/entities/preference'
import type { RTCSessionFacade } from '@/entities/webrtc'

import { startWatching, stopWatching } from './use-rtc-video-stats'

const tryStartWatching = (facade: RTCSessionFacade) => {
  const peerConnection = facade.session.connection as RTCPeerConnection | null
  if (!peerConnection || peerConnection.connectionState === 'closed') return

  startWatching(facade.sessionId, peerConnection)
}

export const useVideoStatsSessionWatcher = (facade: Ref<RTCSessionFacade | undefined>) => {
  const { preferences } = usePreferencesStore()
  const isEnabled = computed(() => isShowVideoStreamStats(preferences[PreferenceKey.SHOW_VIDEO_STREAM_STATS]))

  let peerConnectionListener: ((event: { peerconnection: RTCPeerConnection }) => void) | undefined

  const cleanupPeerConnectionListener = () => {
    if (facade.value?.session && peerConnectionListener) {
      facade.value.session.removeListener('peerconnection', peerConnectionListener)
      peerConnectionListener = undefined
    }
  }

  const setupPeerConnectionListener = (sessionFacade: RTCSessionFacade) => {
    cleanupPeerConnectionListener()

    peerConnectionListener = (event) => {
      startWatching(sessionFacade.sessionId, event.peerconnection as RTCPeerConnection)
    }

    sessionFacade.session.addListener('peerconnection', peerConnectionListener)
  }

  watch([facade, isEnabled], ([sessionFacade, enabled], _, onCleanup) => {
    cleanupPeerConnectionListener()

    if (!sessionFacade || !enabled) {
      if (sessionFacade) stopWatching(sessionFacade.sessionId)
      return
    }

    tryStartWatching(sessionFacade)
    setupPeerConnectionListener(sessionFacade)

    onCleanup(() => {
      cleanupPeerConnectionListener()
      stopWatching(sessionFacade.sessionId)
    })
  }, { immediate: true })

  onBeforeUnmount(() => {
    cleanupPeerConnectionListener()
    if (facade.value?.sessionId) {
      stopWatching(facade.value.sessionId)
    }
  })
}
